import _ from 'lodash'
import { toRaw } from 'vue'
import Draggable from 'vuedraggable'
import DurationInput from '../../../common/duration-input/duration-input-tmpl'
import { tagsStore } from '../../../../pinia/stores/tagsStore'
import { mapState } from 'pinia'

export default {
  name: 'projecteTemplatePages',
  inject: [
    '$currentUser',
    '$utils'
  ],
  components: {
    Draggable, DurationInput
  },
  props: {
    projectTemplateProp: {
      default () { return null }
    },
    currentPageNumberProp: {
      default () { return 0 }
    },
    menuItemInProgressRequestsProp: {
      default () { return [] }
    },
    projectOrientationProp: {
      default () { return 'horizontal' }
    },
    projectTemplatePagesIFramePreviewProp: {
      default () { return null }
    },
    tagsSummaryForSuggestionsProp: {
      type: Array,
      default () { return [] }
    }
  },
  watch: {
    projectTemplateProp (newVal, prevVal) {
      this.setDefaultData(newVal)
      this.postMessageToSVG({ allPages: true })

      // If current page is an overlay then update siblings pages
      const currentPageDetails = (newVal && newVal.data && newVal.data.pages) ? newVal.data.pages[this.currentPageNumberProp] : false
      if ((currentPageDetails && currentPageDetails.settings) && (currentPageDetails.settings.overlay)) {
        this.pages.forEach((pageData, pageNumber) => {
          if (pageNumber !== this.currentPageNumberProp) {
            this.postMessageToSVG({ pageNumber })
          }
        })
      }
    },
    currentPageNumberProp (newVal) {
      this.currentPageNumber = newVal
    },
    menuItemInProgressRequestsProp: {
      handler (val) {
        this.menuItemInProgressRequests = val
      },
      deep: true
    },
    projectOrientationProp (newVal) {
      this.projectOrientation = newVal
      this.postMessageToSVG({ allPages: true })
    },
    tagsSummaryForSuggestionsProp: {
      handler (newVal) {
        this.tagsSummaryForSuggestions = newVal
      },
      deep: true
    }
  },
  mixins: [],
  data () {
    return {
      projectTemplate: this.projectTemplateProp,
      pages: [],
      currentPageNumber: this.currentPageNumberProp,
      pagesSortActive: false,
      pagesBeforeSortStart: [],
      menuItemInProgressRequests: this.menuItemInProgressRequestsProp,
      hoveredPage: null,
      selectedPageBeforeSort: {},
      projectOrientation: this.projectOrientationProp,
      customResolution: {
        width: 0,
        height: 0,
        custom: false,
        customOrientation: 'horizontal',
        ui: { width: null, height: null, aspectRatio: null, elStyle: {} }
      },
      newPageIconVisible: true,
      deletePageIconVisible: true,
      sendingMessageToSVG: false,
      controlArrows: {
        scrollLeft: false,
        scrollRight: false
      },
      tagsSummaryForSuggestions: this.tagsSummaryForSuggestionsProp,
      tagsWithColors: []
    }
  },
  methods: {
    filterOutByUserRights (pageUID, tags) {
      // tags and this.tagsWithColors.tags both has
      // the same source: page.tags, so they share same index as well

      const tagsWithColors = this.tagsWithColors.find(twc => twc.page === pageUID)
      if (!tagsWithColors) {
        return []
      }

      // FIXME: code below doesn't make any sense and should be refactored to Pinia later on
      // and maintain colors there without separation
      return _.chain(tags)
        .filter((_, n) => !tagsWithColors.tags[n].hideByTagUserRights)
        .map((tag, n) => tagsWithColors.tags[n])
        .value()
    },
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('project-template-page-change', { action, payload: { data } })
      }
    },
    setDefaultData (newVal) {
      this.projectTemplate = newVal
      this.setCustomResolution(newVal)
      this.handleNewPageIconVisibility(newVal)
      this.handleDeletePageIconVisibility()
      this.pages = newVal.data && newVal.data.pages.length ? this.filterPages(newVal.data.pages) : []
      this.toggleScrollArrows()
      this.handlePageTagsOverview()

      this.$nextTick(_ => { this.$utils.updatePageComponentStyle({ name: 'createEditProjectPage' }) })
    },
    setCustomResolution (projectTemplate) {
      if (projectTemplate.data && (projectTemplate.data.width && projectTemplate.data.height)) {
        let maxWidth = 0
        let maxHeight = 0
        const minCardPerRow = 3
        const marginsTotalVal = 6 * minCardPerRow

        // Adjust maxwidth and maxheight
        if (projectTemplate.data.width > projectTemplate.data.height) {
          maxWidth = ($('#projectTemplatePageSortWrapper').width() / minCardPerRow) - marginsTotalVal
          maxHeight = this.$utils.is1920View() ? 45 : 42
        } else {
          maxWidth = this.$utils.is1920View() ? 45 : 42
          maxHeight = ($('#projectTemplatePageSortWrapper').width() / minCardPerRow) - marginsTotalVal
        }

        const calculateSizeForUI = this.$utils.calculateAspectRatioFit({
          srcWidth: projectTemplate.data.width,
          srcHeight: projectTemplate.data.height,
          maxWidth,
          maxHeight,
          projectPagePreview: true
        })

        let wrapperWidth = 'auto'
        let wrapperHeight = 'auto'

        // If portrait set a fixed height, otherwise set a fixed width
        if (calculateSizeForUI.cardWidth < calculateSizeForUI.cardHeight) {
          wrapperHeight = `${calculateSizeForUI.cardHeight}px`
        } else {
          wrapperWidth = `${calculateSizeForUI.cardWidth}px`
        }

        this.customResolution = {
          width: projectTemplate.data.width,
          height: projectTemplate.data.height,
          custom: true,
          customOrientation: calculateSizeForUI.cardWidth < calculateSizeForUI.cardHeight ? 'vertical' : 'horizontal',
          ui: {
            width: calculateSizeForUI.cardWidth,
            height: calculateSizeForUI.cardHeight,
            aspectRatio: calculateSizeForUI.aspectRatio,
            elStyle: { width: `${calculateSizeForUI.cardWidth}px`, height: `${calculateSizeForUI.cardHeight}px` },
            wrapperStyle: {
              width: wrapperWidth,
              height: wrapperHeight
            }
          }
        }
        this.showMediaOrientationIcon = false
      }
    },
    filterPages (pages) {
      for (const [pageIndx, pageItem] of pages.entries()) {
        if (!pageItem.order) {
          const topSiblingDetails = (pages[pageIndx - 1]) ? pages[pageIndx - 1] : false
          const bottomSiblingDetails = (pages[pageIndx + 1]) ? pages[pageIndx + 1] : false
          const topSiblingOrder = topSiblingDetails ? topSiblingDetails.order : false
          const bottomSiblingOrder = bottomSiblingDetails ? bottomSiblingDetails.order : false
          pageItem.order = this.$utils.calculateSortOrderValue({ topSiblingOrder, bottomSiblingOrder, previousOrderVal: 0 })
        }

        if (!pageItem._edit) pageItem._edit = {}

        pageItem._edit.sortKeyWithIndx = `${pageItem.order}_${pageIndx}`
      }

      return pages
    },
    handleNewPageIconVisibility (projectTemplate) {
      this.newPageIconVisible = true

      if (projectTemplate.data && projectTemplate.data.skin) {
        this.newPageIconVisible = false
      } else if (this.$currentUser.hasGroupUserLimited()) {
        this.newPageIconVisible = false
      }

      this.$nextTick(_ => {
        this.$utils.updatePageComponentStyle({ name: 'createEditProjectPage' })
        this.$nextTick(_ => { this.$utils.updatePageComponentStyle({ name: 'createEditProjectPage' }) })
      })
      this.$utils.updatePageComponentStyle({ name: 'createEditProjectPage' })
    },
    handleDeletePageIconVisibility () {
      this.deletePageIconVisible = true

      if (this.$currentUser.hasGroupUserLimited()) {
        this.deletePageIconVisible = false
      }
    },
    resetCustomResolution () {
      this.customResolution = {
        width: 0,
        height: 0,
        custom: false,
        ui: {
          width: null,
          height: null,
          aspectRatio: null,
          elStyle: {}
        }
      }
      this.showMediaOrientationIcon = true
    },
    handleSVGOnLoad () {
      this.postMessageToSVG({ type: 'iframeOnLoad', allPages: true })

      // If first page is locked by user tags rights, find another page and set as default
      if (this.pages.length && this.pages[0]._edit.lockedByUserTags) {
        const defaultPageNumber = this.pages.findIndex(page => !page._edit.lockedByUserTags)
        this.handleProjectPageClick(defaultPageNumber)
      }
    },
    addMessageListener () {
      if (!this.handleSVGMessageEventListenerFunction) {
        this.handleSVGMessageEventListenerFunction = this.handleSVGMessage.bind(this)
        window.addEventListener('message', this.handleSVGMessageEventListenerFunction)
      }
    },
    removeMessageListener () {
      if (this.handleSVGMessageEventListenerFunction) {
        window.removeEventListener('message', this.handleSVGMessageEventListenerFunction)
        this.handleSVGMessageEventListenerFunction = null
      }
    },
    handleSVGMessage (event) {
    },
    handleProjectPageAddClick () {
      this.emitEvent('ADD_NEW_PAGE', { pageNumber: 0 })
      this.$utils.updatePageComponentStyle({ name: 'createEditProjectPage' })
    },
    handleProjectPageClick  (pageNumber) {
      this.emitEvent('NEW_PAGE_SELECTED', { pageNumber })
    },
    handleProjectPageDeleteClick  (pageNumber) {
      // Adjust current Page number
      let newScreenIndex = this.currentPageNumber
      if ((parseInt(this.currentPageNumber) >= parseInt(pageNumber))) {
        newScreenIndex = parseInt(this.currentPageNumber) - 1
      }
      this.currentPageNumber = Math.max(0, newScreenIndex)
      this.emitEvent('DELETE_PAGE', { pageNumber, newScreenIndex: this.currentPageNumber })
      this.$utils.updatePageComponentStyle({ name: 'createEditProjectPage' })
    },
    postMessageToSVG (params) {
      if (params.pageNumber && ['number', 'string'].indexOf(typeof (params.pageNumber)) < 0) return

      const sendMessage = (pageNumber) => {
        const projectTemplateData = this.$utils.clone(this.projectTemplate.data)
        projectTemplateData.pages = this.pages
        projectTemplateData.dir = this.projectOrientation
        const msg = {
          page: parseInt(pageNumber),
          setOptions: toRaw(this.projectTemplate.setOptions),
          projectOptions: {
            ...toRaw(projectTemplateData),
            pages: toRaw(projectTemplateData.pages)
          }
        }

        const targetEl = $(`#projectPagePreview_${pageNumber}`)
        if (targetEl.length && targetEl.hasClass('content-loaded')) {
          console.log(' -- Sending message to a page:', msg)
          targetEl[0].contentWindow.postMessage(msg, '*')
          targetEl.addClass('onload-postmessage-sent')
        }
      }

      if (params.allPages) {
        this.pages.forEach((pageData, pageNumber) => { sendMessage(pageNumber) })
      } else {
        sendMessage(params.pageNumber)
      }

      window.setTimeout(() => { this.sendingMessageToSVG = false }, 500)
    },
    handlePageIconSortStart (sortEvtVal) {
      this.pagesSortActive = true

      if (sortEvtVal && sortEvtVal.item && $(sortEvtVal.item).hasClass('new-page-icon')) {
        this.pagesSortActive = false
        return
      }

      this.pagesBeforeSortStart = this.$utils.clone(this.pages)
      this.selectedPageBeforeSort = this.$utils.clone(this.pagesBeforeSortStart[this.currentPageNumber])
    },
    handlePageIconSortEnd (sortEvtVal) {
      this.pagesSortActive = false
      if (sortEvtVal && sortEvtVal.item && $(sortEvtVal.item).hasClass('new-page-icon')) return

      // Previous Data
      const pagesBeforeSortStart = this.$utils.clone(this.pagesBeforeSortStart)
      const previousIndexInList = sortEvtVal.oldIndex
      const previousOrderVal = pagesBeforeSortStart[previousIndexInList].order
      const selectedPageBeforeSort = this.$utils.clone(this.selectedPageBeforeSort)

      const pages = this.$utils.clone(this.pages)
      const currentIndexInList = sortEvtVal.newIndex
      const topSiblingDetails = (pages[currentIndexInList - 1]) ? pages[currentIndexInList - 1] : false
      const bottomSiblingDetails = (pages[currentIndexInList + 1]) ? pages[currentIndexInList + 1] : false
      const topSiblingOrder = topSiblingDetails ? topSiblingDetails.order : false
      const bottomSiblingOrder = bottomSiblingDetails ? bottomSiblingDetails.order : false

      pages[currentIndexInList].order = this.$utils.calculateSortOrderValue({ topSiblingOrder, bottomSiblingOrder, previousOrderVal })

      this.pages = this.filterPages(pages)

      const findSelectedPage = this.pages.findIndex((page) => { return page.uid === selectedPageBeforeSort.uid })
      this.emitEvent('PAGE_SORT', { pages: this.pages, newScreenIndex: Math.max(0, findSelectedPage) })

      this.sendingMessageToSVG = true
      window.setTimeout(() => { this.postMessageToSVG({ allPages: true }) }, 500)
    },
    handleMoveCarousel (direction) {
      const content = document.querySelector('.transition-group-container')
      const item = document.querySelector('.page-item')
      content.scrollLeft += (item.clientWidth * direction)

      this.toggleScrollArrows()
    },
    toggleScrollArrows () {
      const content = document.querySelector('.transition-group-container')
      const MAX_SCROLLBALE_VALUE = content.scrollWidth - content.offsetWidth

      // Disbale left arrow if current scroll is at the left-most of the content
      if (content.scrollLeft <= 0) {
        this.controlArrows.scrollLeft = false
      } else {
        this.controlArrows.scrollLeft = true
      }

      // Disable right arrow if current scroll is at the right-most of the content
      if (content.scrollLeft >= MAX_SCROLLBALE_VALUE) {
        this.controlArrows.scrollRight = false
      } else {
        this.controlArrows.scrollRight = true
      }
    },
    handleFocusOut (input, pageIndex) {
      this.pages[pageIndex].settings.dur = input
    },
    handleChangeDuration (value, pageIndex) {
      this.pages[pageIndex].settings.dur = value
      this.emitEvent('PAGE_PROPERTIES_UPDATED', { pages: this.pages })
    },
    handlePageTagsOverview () {
      this.tagsWithColors = this.pages.map((page) => {
        let formattedPageTags = []

        if (page.tags) {
          formattedPageTags = page.tags.map((tag) => {
            return this.$utils.formatPageTagWithColors(tag, this.tagsSummaryForSuggestions, this.storeOnLoadTagsFromSlider)
          })

          formattedPageTags = this.$utils.filterTagOptionsForUI({ tags: formattedPageTags })
        }

        return {
          page: page.uid,
          tags: formattedPageTags
        }
      })
    },
    /**
     * Formats the duration in the same way as it is formatted in the right-side settings.
     * Returns a string in the format 00:00:10.
     * @returns {string} The formatted duration.
     */
    handleDurationFormatting (duration) {
      return this.$utils.formatDurationToMinutesAndSeconds({ duration: this.$utils.convertDurationToSeconds(duration) })
    }
  },
  computed: {
    sortDraggableOptions () {
      return { animation: 250, group: 'description', disabled: false, ghostClass: 'ghost' }
    },
    isUserLimited () {
      return this.$currentUser.hasGroupUserLimited()
    },
    ...mapState(tagsStore, ['tagSliderTagCombinations']),
    storeOnLoadTagsFromSlider () {
      const onLoadTagSlider = this.tagSliderTagCombinations
      let res = onLoadTagSlider

      // Make sure it's a flat array
      if ((typeof onLoadTagSlider === 'object' && !Array.isArray(onLoadTagSlider) && onLoadTagSlider !== null)) {
        res = []
        for (const tagType in onLoadTagSlider) {
          for (const arrIndx in onLoadTagSlider[tagType]) {
            const tag = onLoadTagSlider[tagType][arrIndx]
            tag.type = tagType
            res.push(tag)
          }
        }
      }

      return res
    }
  },
  beforeUnmount () {
    this.removeMessageListener()
  },
  mounted () {
    this.addMessageListener()
    this.setDefaultData(this.projectTemplateProp)
  }
}
